import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import IntroViz from "./components/introduction/IntroViz";
import ReportingProblemViz from "./components/reportingProblem/ReportingProblemViz";
import MirroringViz from "./components/mirroring/MirroringViz";
import ClassificationViz from "./components/classification/ClassificationViz";
import { SectionContainer } from "./components/SharedUI";

const KeyboardContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  outline: none;
`;

const CenteredSectionContainer = styled(SectionContainer)`
  justify-content: center;
  align-items: center;
`;

const VizViewport = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
`;

const VizLayer = styled.div<{ active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ active }) => (active ? 1 : 0)};
  visibility: ${({ active }) => (active ? "visible" : "hidden")};
  pointer-events: ${({ active }) => (active ? "auto" : "none")};
  transition: opacity 150ms ease-out;
`;

const MountGate = styled.div<{ ready: boolean }>`
  width: 100%;
  height: 100%;
  opacity: ${({ ready }) => (ready ? 1 : 0)};
  visibility: ${({ ready }) => (ready ? "visible" : "hidden")};
  transition: opacity 120ms ease-out;
`;

type IntroStep =
  | "intro"
  | "country-A-pov"
  | "country-B-pov"
  | "discrepancy"
  | "discrepancy2";

const introSteps: IntroStep[] = [
  "intro",
  "country-A-pov",
  "country-B-pov",
  "discrepancy",
  "discrepancy2",
];

const reportingSteps = [
  "ideal",
  "actual",
  "reality-both-reported-aligned",
  "reality-add-both-misaligned",
  "reality-add-importer-only",
  "reality-add-exporter-only",
  "reality",
] as const;

const mirroringSteps = [
  "mirroring-intro",
  "average",
  "methodology-intro",
  "reliability",
  "mirroring-outro",
] as const;

const classificationSteps = [
  "transition",
  "classification-intro",
  "example",
  "mapping-intro",
  "mapping-static",
  "mapping-issues",
  "gl-method-2",
] as const;

type TimelineItem =
  | { section: "introduction"; step: IntroStep }
  | { section: "reporting-problem"; step: (typeof reportingSteps)[number] }
  | { section: "mirroring"; step: (typeof mirroringSteps)[number] }
  | { section: "classification"; step: (typeof classificationSteps)[number] };

export default function PresentationMode() {
  const timeline = useMemo<TimelineItem[]>(
    () => [
      ...introSteps.map((step) => ({
        section: "introduction" as const,
        step,
      })),
      ...reportingSteps.map((step) => ({
        section: "reporting-problem" as const,
        step,
      })),
      ...mirroringSteps.map((step) => ({
        section: "mirroring" as const,
        step,
      })),
      ...classificationSteps.map((step) => ({
        section: "classification" as const,
        step,
      })),
    ],
    [],
  );

  const [timelineIndex, setTimelineIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let frameTwo = 0;
    const frameOne = window.requestAnimationFrame(() => {
      frameTwo = window.requestAnimationFrame(() => {
        setIsReady(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(frameOne);
      if (frameTwo) {
        window.cancelAnimationFrame(frameTwo);
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        setTimelineIndex((prev) => Math.min(prev + 1, timeline.length - 1));
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        setTimelineIndex((prev) => Math.max(prev - 1, 0));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [timeline.length]);

  const current = timeline[timelineIndex];

  const introStep: IntroStep =
    current.section === "introduction" ? current.step : "discrepancy2";
  const reportingStep =
    current.section === "reporting-problem" ? current.step : "reality";
  const mirroringStep =
    current.section === "mirroring"
      ? current.step
      : current.section === "classification"
        ? "mirroring-outro"
        : "mirroring-intro";
  const classificationStep =
    current.section === "classification" ? current.step : "gl-method-2";

  return (
    <KeyboardContainer className="trade-methodology-page" tabIndex={0}>
      <MountGate ready={isReady}>
        <VizViewport>
          <VizLayer active={current.section === "introduction"}>
            <CenteredSectionContainer>
              <IntroViz currentStep={introStep} width={0} height={0} />
            </CenteredSectionContainer>
          </VizLayer>

          <VizLayer active={current.section === "reporting-problem"}>
            <CenteredSectionContainer>
              <ReportingProblemViz currentStep={reportingStep} />
            </CenteredSectionContainer>
          </VizLayer>

          <VizLayer active={current.section === "mirroring"}>
            <CenteredSectionContainer>
              <MirroringViz currentStep={mirroringStep} />
            </CenteredSectionContainer>
          </VizLayer>

          <VizLayer active={current.section === "classification"}>
            <CenteredSectionContainer>
              <ClassificationViz currentStep={classificationStep} />
            </CenteredSectionContainer>
          </VizLayer>
        </VizViewport>
      </MountGate>
    </KeyboardContainer>
  );
}
